import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { RELEASE, whatsNewStorageKey, whatsNewDecision } from "@/config/release";

// Shows a concise one-time "Novità" summary AFTER the new release is actually running
// (this component ships inside the new bundle, so it only executes on the new version).
export const WhatsNewDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const decidedRef = useRef(false); // decide/show at most once per session (no re-show loops)
  const ackingRef = useRef(false);  // guard rapid double dismissal (idempotent acknowledge)

  // Only a fully resolved, authenticated user yields a stable acknowledgement identity.
  // While unresolved (null) or offline ({offline:true}) we treat ack state as UNKNOWN and
  // never show prematurely — "not loaded yet" must not be read as "not acknowledged".
  const authed = user && typeof user === "object" && !user.offline;
  const uid = authed ? (user.id || user.email || null) : null;

  useEffect(() => {
    if (!uid || decidedRef.current) return;
    let cancelled = false;
    const evaluate = async () => {
      let updatePending = false;
      try {
        if ("serviceWorker" in navigator && navigator.serviceWorker.getRegistration) {
          const reg = await navigator.serviceWorker.getRegistration();
          updatePending = !!(reg && reg.waiting);
        }
      } catch (e) { /* SW unavailable: assume no pending update */ }
      if (cancelled) return;
      // Read the persisted acknowledgement for THIS release. If storage is unreadable we
      // fail closed (treat as acknowledged) so we never nag on a repeating loop.
      let acknowledged = true;
      try { acknowledged = !!localStorage.getItem(whatsNewStorageKey(uid, RELEASE.releaseId)); } catch (e) { acknowledged = true; }
      if (whatsNewDecision({ release: RELEASE, userReady: true, updatePending, acknowledged })) {
        decidedRef.current = true;
        setOpen(true);
      }
    };
    evaluate();
    const onUpd = () => setOpen(false); // a newer update just appeared -> defer notes until adopted
    window.addEventListener("k9swupdate", onUpd);
    return () => { cancelled = true; window.removeEventListener("k9swupdate", onUpd); };
  }, [uid]);

  const ack = () => {
    if (ackingRef.current) return; // rapid double-click/tap -> only persist once
    ackingRef.current = true;
    try { localStorage.setItem(whatsNewStorageKey(uid, RELEASE.releaseId), new Date().toISOString()); } catch (e) { /* ignore */ }
    decidedRef.current = true; // do not reopen this session even if the effect re-runs
    setOpen(false);
  };

  if (!uid) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) ack(); }}>
      <DialogContent className="max-w-md" data-testid="whatsnew-modal">
        <DialogHeader><DialogTitle className="font-head">{RELEASE.title}</DialogTitle></DialogHeader>
        {RELEASE.heading && <p className="text-sm font-semibold -mt-1" data-testid="whatsnew-heading">{RELEASE.heading}</p>}
        <div className="space-y-2 text-sm text-foreground/90">
          {(RELEASE.paragraphs || []).map((p, i) => <p key={i} data-testid={`whatsnew-note-${i}`}>{p}</p>)}
        </div>
        {RELEASE.secondaryHeading && (
          <>
            <p className="text-sm font-semibold pt-1" data-testid="whatsnew-secondary-heading">{RELEASE.secondaryHeading}</p>
            <div className="space-y-2 text-sm text-foreground/90">
              {(RELEASE.secondaryParagraphs || []).map((p, i) => <p key={i} data-testid={`whatsnew-secondary-note-${i}`}>{p}</p>)}
            </div>
          </>
        )}
        <DialogFooter>
          <Button className="rounded-full" data-testid="whatsnew-ack" onClick={ack}>Ho capito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
